<template>
	<button class="lg:hidden" aria-label="menu" @click="isSideMenuOpened = true">
		<span class="sr-only">Open main menu</span>
		<Bars3Icon class="h-6 w-6" aria-hidden="true" />
	</button>
	<TransitionRoot :show="isSideMenuOpened" appear as="template">
		<Dialog as="div" class="lg:hidden" @close="close">
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="duration-300 ease-out"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
			</TransitionChild>
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="-translate-x-full"
				enter-to="translate-x-0"
				leave="duration-300 ease-out"
				leave-from="translate-x-0"
				leave-to="-translate-x-full"
			>
				<DialogPanel
					class="fixed inset-y-0 z-50 left-0 w-full overflow-y-auto bg-white pt-6 sm:max-w-sm sm:ring-1 sm:ring-gray-dark"
				>
					<div class="relative h-fit pb-10">
						<div class="px-4 flex items-center justify-between">
							<div>
								<ChevronLeftIcon
									v-if="
										currentNavigationElement || currentChildNavigationElement
									"
									class="cursor-pointer h-6 w-6 text-gray-700"
									aria-hidden="true"
									@click="goBack"
								/>
							</div>
							<div>
								<h4 class="font-medium text-lg">Menu</h4>
							</div>
							<button
								type="button"
								class="-m-2.5 rounded-md p-2.5 text-gray-700 outline-none"
								@click="close"
							>
								<span class="sr-only">Close menu</span>
								<XMarkIcon class="h-6 w-6" aria-hidden="true" />
							</button>
						</div>
						<div class="border-t border-gray-dark mt-6 flow-root relative">
							<div class="divide-y divide-gray-500/10">
								<div class="grid">
									<template
										v-if="
											!currentNavigationElement &&
											!currentChildNavigationElement
										"
									>
										<div
											v-for="navigationElement in menuCategoriesNorce"
											:key="navigationElement.CategoryId"
											class="font-medium cursor-pointer flex justify-between items-center border-b border-gray-dark px-4 py-3 text-base leading-7 text-gray-700 hover:bg-gray"
											:class="
												navigationElement.order ? navigationElement.order : ''
											"
										>
											<NuxtLink
												:to="
													localePath(
														`/${createCategoryLink({
															category: navigationElement
														})}`
													)
												"
												class="flex-grow"
												@click="close"
											>
												{{ navigationElement.Name }}
											</NuxtLink>
											<div
												class="w-1/4 flex justify-end"
												@click="firstNavigate(navigationElement)"
											>
												<ChevronRightIcon
													v-if="navigationElement.Children?.length"
													class="h-6 w-6"
													aria-hidden="true"
												/>
											</div>
										</div>
										<!-- additional secondary storyblok menu items -->
										<StoryblokComponent
											v-for="blok in menuCategories"
											:key="blok._uid"
											:blok="blok"
											class="font-medium cursor-pointer flex justify-between items-center border-b border-gray-dark px-4 block py-3 text-base leading-7 text-gray-700 hover:bg-gray"
											:class="blok.order ? blok.order : ''"
											@closeMobileMenu="close"
										/>
										<!-- end additional secondary storyblok menu items -->
										<div class="my-6 mx-4 flex flex-col gap-8">
											<template
												v-for="navigationElement in menuCategoriesNorce"
												:key="navigationElement.id"
											>
											</template>
										</div>
									</template>
									<template
										v-else-if="
											currentNavigationElement && !currentChildNavigationElement
										"
									>
										<div
											v-for="navigationElement in currentNavigationElement.Children"
											:key="navigationElement.CategoryId"
											class="font-medium cursor-pointer flex justify-between items-center border-b border-gray-dark px-4 py-3 text-base leading-7 hover:bg-gray"
										>
											<NuxtLink
												:to="
													localePath(
														`/${createCategoryLink({
															category: navigationElement
														})}`
													)
												"
												class="flex-grow"
												@click="close"
											>
												{{ navigationElement.Name }}
											</NuxtLink>
											<div
												class="w-1/4 flex justify-end"
												@click="secondNavigate(navigationElement)"
											>
												<ChevronRightIcon
													v-if="navigationElement.Children?.length"
													class="h-6 w-6"
													aria-hidden="true"
												/>
											</div>
										</div>
										<div class="my-6 mx-4 flex flex-col gap-8">
											<!-- <template v-for="(navigationElement, index) in currentNavigationElement.Children" :key="navigationElement.id">
                        media
                      </template> -->
										</div>
									</template>
									<template v-else-if="currentChildNavigationElement">
										<NuxtLink
											v-for="navigationElement in currentChildNavigationElement.Children"
											:key="navigationElement.CategoryId"
											:to="
												localePath(
													`/${createCategoryLink({
														category: navigationElement
													})}`
												)
											"
											class="cursor-pointer flex justify-between items-center border-b border-gray-dark px-4 block py-3 text-base leading-7 hover:bg-gray"
											@click="close"
										>
											{{ navigationElement.Name }}
										</NuxtLink>
									</template>
								</div>
							</div>
						</div>
					</div>
				</DialogPanel>
			</TransitionChild>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import {
	XMarkIcon,
	Bars3Icon,
	ChevronLeftIcon,
	ChevronRightIcon
} from "@heroicons/vue/24/outline";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { MenuLink } from "~/types/storyblok/storyblok";

interface ExtendedCategory extends Category {
	order?: string;
}

interface Props {
	menuCategoriesNorce: ExtendedCategory[] | undefined;
	menuCategories: MenuLink[] | undefined;
}

const { menuCategoriesNorce } = defineProps<Props>();

const { createCategoryLink } = useCategory();

const localePath = useLocalePath();
const currentNavigationElement = ref<Category | null>();
const currentChildNavigationElement = ref<Category | null>();

const isSideMenuOpened = inject("isSideMenuOpened", ref(false));

const close = () => {
	isSideMenuOpened.value = false;
	currentNavigationElement.value = null;
	currentChildNavigationElement.value = null;
};

const goBack = () => {
	if (currentChildNavigationElement.value) {
		currentChildNavigationElement.value = null;
	} else if (currentNavigationElement.value) {
		currentNavigationElement.value = null;
	}
};

const firstNavigate = (value: Category) => {
	currentNavigationElement.value = value;
	if (!value.Children) {
		close();
	}
};

const secondNavigate = (value: Category) => {
	currentChildNavigationElement.value = value;
	if (!value.Children) {
		close();
	}
};
</script>
